import React, { useState, useEffect, useMemo } from 'react';

/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values */
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { ContentAppearanceType } from '@confluence/content-appearance/entry-points/contentAppearanceTypes.ts';
import {
	useCommentsPanelState,
	AddFooterCommentMode,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { getWidthAndMaxWidth } from '@confluence/comments-util/entry-points/domUtils.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { usePageContentId } from '@confluence/page-context';
import { usePageRecommendationData } from '@confluence/page-recommendations/entry-points/usePageRecommendationData.tsx';

import { usePageContentDimensions } from '../hooks/usePageContentDimensions.ts';
import { hideWhiteSpace } from '../utils/hideWhiteSpace.ts';

import { DraftCommentBox } from './DraftCommentBox.tsx';
import { ObjectAddCommentProfileWrapper } from './ObjectAddCommentProfileWrapper.tsx';

const styles = cssMap({
	stickyDraftViewPageWrapper: {
		position: 'sticky',
		zIndex: 1,
	},
	stickyDraftLivePageWrapper: {
		position: 'fixed',
		zIndex: 500,
	},
	hiddenStyles: {
		display: 'none',
	},
	visibleStyles: {
		display: 'flex',
	},
	commonStyle: {
		alignItems: 'center',
		justifyContent: 'center',
		bottom: token('space.0'),
		backgroundColor: token('elevation.surface'),
	},
	childWrapperStyle: {
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderColor: token('color.border'),
		padding: token('space.200'),
		margin: '0 auto',
	},
	childWrapperStyleWithBorders: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderBottomWidth: 0,
		borderTopLeftRadius: token('radius.large'),
		borderTopRightRadius: token('radius.large'),
		padding: token('space.200'),
		margin: '0 auto',
	},
	zIndexStyle: {
		zIndex: 200,
		left: token('space.0'),
		width: '100%',
	},
});

export type StickyDraftCommentContainerProps = {
	pageWidthType?: string;
};

export const StickyDraftCommentContainer: React.FC<StickyDraftCommentContainerProps> = ({
	pageWidthType,
}) => {
	const { expanded, isObjectCommentWrapperVisible, footerEditorEntry } = useObjectCommentContext();
	const {
		expandCommentContainer,
		setWrapperWidth,
		setIsObjectCommentWrapperVisible,
		setFooterEditorEntry,
	} = useObjectCommentDispatchContext();
	const [contentId] = usePageContentId();
	const [objectCommentWrapperDimensions, setObjectCommentWrapperDimensions] = useState<{
		width: number;
	} | null>(null);

	const pageMode = useGetPageMode();

	const isViewPage = pageMode === PageMode.VIEW;

	// Track page-content-only dimensions for layout alignment and blue background sizing
	const {
		width: pageContentWidth,
		left: pageContentLeft,
		right: pageContentRight,
	} = usePageContentDimensions();

	const { hasRecs } = usePageRecommendationData();

	const { addFooterCommentAdf } = useCommentsPanelState();

	// Hide the sticky draft when the comment wrapper is visible so that when they scroll back up they don't have the full editor visible
	useEffect(() => {
		if (isObjectCommentWrapperVisible && expanded) {
			expandCommentContainer();
			setFooterEditorEntry(AddFooterCommentMode.FOOTER);
		}
	}, [isObjectCommentWrapperVisible, expandCommentContainer, expanded, setFooterEditorEntry]);

	useEffect(() => {
		let intersectionObserver: IntersectionObserver | null = null;
		let resizeObserver: ResizeObserver | null = null;

		const commentWrapperSelector = '[data-testid="object-comment-wrapper"]';

		const calculateObjectCommentWrapperWidth = () => {
			// Find the object-comment-wrapper element and match its dimensions
			const objectCommentWrapper = document.querySelector(commentWrapperSelector);
			if (objectCommentWrapper) {
				const rect = objectCommentWrapper.getBoundingClientRect();
				setObjectCommentWrapperDimensions({
					width: rect.width,
				});
				setWrapperWidth(rect.width);
			}
		};

		const setupObservers = () => {
			const commentWrapper = document.querySelector(commentWrapperSelector);

			if (!commentWrapper) {
				setIsObjectCommentWrapperVisible(false);
				return;
			}

			// Set up intersection observer for visibility tracking
			if (!intersectionObserver) {
				intersectionObserver = new IntersectionObserver(
					([entry]) => {
						// Check if the comment wrapper is visible in the viewport
						// We want to hide the sticky draft when the comment wrapper is visible
						setIsObjectCommentWrapperVisible(entry.isIntersecting);
					},
					{
						// No rootMargin - use default intersection detection
						rootMargin: '0px',
					},
				);
				intersectionObserver.observe(commentWrapper);
			}

			// Set up resize observer for dimension tracking (only for live pages)
			if (!resizeObserver) {
				resizeObserver = new ResizeObserver(() => {
					calculateObjectCommentWrapperWidth();
				});
				resizeObserver.observe(commentWrapper);
			}

			// Calculate initial dimensions for live pages
			calculateObjectCommentWrapperWidth();
		};

		// Try to set up observers immediately
		setupObservers();

		// Set up a single MutationObserver to watch for when the comment wrapper gets added to the DOM
		// This happens when the wrapper is lazy loaded
		const mutationObserver = new MutationObserver(() => {
			const commentWrapper = document.querySelector('[data-testid="object-comment-wrapper"]');
			if (commentWrapper && (!intersectionObserver || (!isViewPage && !resizeObserver))) {
				setupObservers();
			}
		});

		mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			if (intersectionObserver) {
				intersectionObserver.disconnect();
			}
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
			mutationObserver.disconnect();
		};
	}, [isViewPage, setWrapperWidth, setIsObjectCommentWrapperVisible, contentId]);

	const pageContentDimensions = useMemo(
		() => ({
			width: pageContentWidth,
			left: pageContentLeft,
			right: pageContentRight,
		}),
		[pageContentWidth, pageContentLeft, pageContentRight],
	);

	const getMaxWidth = () => {
		if (pageWidthType === undefined) {
			return '100%';
		}
		// pageWidthType is a string like '"default"'
		return JSON.parse(pageWidthType) === ContentAppearanceType.DEFAULT ? '1000px' : 'unset';
	};

	const getStyle = () => {
		if (
			isViewPage &&
			expValEquals('confluence_unify_footer_comments_space_width', 'isEnabled', true)
		) {
			const computedWidths = getWidthAndMaxWidth({
				pageMode,
				pageWidthType,
				hasEndOfPageRecommendations: hasRecs ?? false,
			});
			return {
				width: computedWidths.width,
				maxWidth: computedWidths.maxWidth,
			};
		}
		return {
			maxWidth: isViewPage ? getMaxWidth() : '100%',
			width:
				!isViewPage && objectCommentWrapperDimensions
					? `${objectCommentWrapperDimensions.width}px`
					: '100%',
		};
	};

	// Show the sticky draft if the comment wrapper is not visible and there is an ADF or the editor is expanded
	// This is to prevent the draft comment from disappearing if you delete all of the text
	const showStickyDraft =
		!isObjectCommentWrapperVisible &&
		(addFooterCommentAdf || expanded) &&
		footerEditorEntry !== AddFooterCommentMode.STICKY_DRAFT;

	const expandEditorHandler = () => {
		expandCommentContainer();
		setFooterEditorEntry(AddFooterCommentMode.STICKY_DRAFT);
	};

	if (!showStickyDraft) {
		return;
	}
	// Use hidden / visible styles below to prevent unnecessary unmounting (which can have side effects, specifically for editor)
	return (
		<Box
			xcss={cx(
				isViewPage ? styles.stickyDraftViewPageWrapper : styles.stickyDraftLivePageWrapper,
				showStickyDraft ? styles.visibleStyles : styles.hiddenStyles,
				styles.commonStyle,
				!isViewPage && styles.zIndexStyle,
			)}
			testId="sticky-draft-comment-box"
			id="addCommentButton-draft"
			style={
				pageMode === PageMode.VIEW
					? hideWhiteSpace({
							shouldRenderFixedStyle: true,
							pageContentDimensions,
						})
					: undefined
			}
		>
			<Box
				xcss={
					fg('cc-sticky-draft-comment-borders')
						? styles.childWrapperStyleWithBorders
						: styles.childWrapperStyle
				}
				style={getStyle()}
			>
				<Box>
					<ObjectAddCommentProfileWrapper>
						<Box xcss={cx(!showStickyDraft ? styles.hiddenStyles : styles.visibleStyles)}>
							<DraftCommentBox expandPageCommentEditorHandler={expandEditorHandler} />
						</Box>
					</ObjectAddCommentProfileWrapper>
				</Box>
			</Box>
		</Box>
	);
};
